import { ColumnDef } from "@tanstack/react-table"
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import DatePicker from "react-datepicker"
import { useTranslation } from "react-i18next"
import { useDispatch } from "react-redux"

import { handleToast } from "utils/messages"
import { fromPercent } from "utils/numbers"

import useStateGetter from "hooks/useStateGetter"
import useUserFromToken from "hooks/useUserFromToken"

import { getPlants } from "../../../companies/service/api"
import { Company } from "../../../companies/types"
import { compositionGetByCompanyId } from "../../../compositions/service/api"
import { CompositionType } from "../../../compositions/constants"
import { Composition } from "../../../compositions/types"
import { fetchCountries } from "../../../countries/actions"
import { Country } from "../../../countries/types"
import { filterCompanyCompositions } from "../../../genericCompositions/utils"
import { getProducts } from "../../../products/service/api"
import { Product } from "../../../products/types"
import {
  deleteSubmission,
  getSubmissions,
  putSubmission,
} from "../../service/api"
import { Submission, SubmissionType } from "../../types"
import SubmissionGrid, { GridRow } from "./SubmissionGrid"
import { useGridSubmit } from "./useGridSubmit"

// Tab values match SubmissionListTable.
export type ManagedTab =
  | "input"
  | "output"
  | "sortingInput"
  | "sortingOutput"
  | "convertingInput"
  | "convertingOutput"

type ManagedRow = Submission & { id?: number }
type LocalRow = GridRow<ManagedRow>

type Props = {
  tab: ManagedTab
  companyId?: number
  dateStart?: string
  dateEnd?: string
  status?: string
}

const flattenProducts = (
  products: Product[],
): { id: number; title: string }[] => {
  const out: { id: number; title: string }[] = []
  const walk = (list: Product[], prefix = "") => {
    for (const p of list) {
      const title = prefix ? `${prefix} / ${p.title}` : p.title
      out.push({ id: p.id, title })
      if (p.children?.length) walk(p.children, title)
    }
  }
  walk(products)
  return out
}

const filterFor = (
  tab: ManagedTab,
): { isInput: boolean; type: SubmissionType } => {
  switch (tab) {
    case "input":
      return { isInput: true, type: SubmissionType.RECYCLING }
    case "output":
      return { isInput: false, type: SubmissionType.RECYCLING }
    case "sortingInput":
      return { isInput: true, type: SubmissionType.SORTING }
    case "sortingOutput":
      return { isInput: false, type: SubmissionType.SORTING }
    case "convertingInput":
      return { isInput: true, type: SubmissionType.CONVERTING }
    case "convertingOutput":
      return { isInput: false, type: SubmissionType.CONVERTING }
  }
}

const computeNet = (gross?: number, contaminationPct?: number | null): number => {
  if (gross == null || Number.isNaN(gross)) return 0
  const c = contaminationPct ?? 0
  return Math.max(0, parseFloat((gross - fromPercent(gross, c)).toFixed(2)))
}

const ManageSubmissionsGrid: React.FC<Props> = ({
  tab,
  companyId,
  dateStart,
  dateEnd,
  status,
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { isSorter, isSorterAndRecycler } = useUserFromToken()

  const [rows, setRows] = useState<LocalRow[]>([])
  const [isEditing, setIsEditing] = useState(false)
  const [products, setProducts] = useState<{ id: number; title: string }[]>([])
  const [plants, setPlants] = useState<Company[]>([])
  const [compositionsByCompany, setCompositionsByCompany] = useState<
    Record<number, Composition[]>
  >({})
  const [contaminationByKey, setContaminationByKey] = useState<Record<string, number | undefined>>({})
  const fetchSeqRef = useRef(0)

  const countriesEu = useStateGetter<Country[]>(["country", "countries"]) ?? []
  const countries = useMemo<Country[]>(
    () => [
      ...countriesEu,
      { id: 999, name: "Other", code: "NONEU" } as Country,
    ],
    [countriesEu],
  )

  const buildPayload = useCallback(
    (row: ManagedRow & { _key?: string }) => {
      const editedPct = contaminationByKey[row._key ?? ""]
      const derivedPct = (() => {
        const gross = row.volumeGross
        const net = row.volumeNet
        if (gross == null || gross <= 0 || net == null) return 0
        return Math.max(0, Math.round((1 - net / gross) * 10000) / 100)
      })()
      const contaminationPercentage = editedPct != null ? editedPct : derivedPct
      const volumeNet =
        tab === "input" || tab === "sortingInput"
          ? editedPct != null
            ? computeNet(row.volumeGross, editedPct)
            : row.volumeNet
          : row.volumeNet

      const country = countries.find((c) => c.id === (row.country?.id ?? row.countryId))
      const isMaterialFromOutsideEurope = country ? !country.isEu : row.isMaterialFromOutsideEurope ?? false

      return {
        id: row.id!,
        companyId: row.company?.id ?? companyId!,
        countryId: row.country?.id ?? null,
        compositionId: row.composition?.id ?? null,
        materialsGroupId: row.materialsGroup?.id ?? null,
        collectionDate: row.collectionDate,
        volumeGross: row.volumeGross,
        contaminationUnit: row.contaminationUnit,
        contaminationPercentage,
        volumeNet,
        grossOutputEU: row.grossOutputEU ?? 0,
        grossOutputNonEU: row.grossOutputNonEU ?? 0,
        netOutputEU: row.netOutputEU,
        netOutputNonEU: row.netOutputNonEU,
        virginAdditives: row.virginAdditives ?? 0,
        isInput: row.isInput,
        administrationNumber: row.administrationNumber ?? "",
        isMaterialFromOutsideEurope,
        type: row.type,
        wasteType: row.wasteType ?? "",
        productId: row.productId ?? 0,
        estimatedShareExportedInsideEu: row.estimatedShareExportedInsideEu ?? 0,
        estimatedShareExportedOutsideEu: row.estimatedShareExportedOutsideEu ?? 0,
        totalDemonstratedDuringAudit: row.totalDemonstratedDuringAudit ?? 0,
        totalPercentageOfRecycledPlasticsInFrance:
          row.totalPercentageOfRecycledPlasticsInFrance ?? 0,
      }
    },
    [companyId, contaminationByKey, countries, tab],
  )

  const { states, isSubmitting, submitAll, clearRowState } =
    useGridSubmit<ManagedRow>({
      validate: (row) => {
        if (row.id == null) return null
        return []
      },
      create: async () => ({ errors: [t("Cannot create from Manage view")] }),
      update: async (row: ManagedRow & { id: number; _key?: string }) => {
        const { data, errors } = await putSubmission(buildPayload(row) as any)
        if (errors) return { errors: errors.map((e) => e.message) }
        return { id: data?.id ?? row.id! }
      },
    })

  const fetchRows = useCallback(async () => {
    const seq = ++fetchSeqRef.current
    if (!companyId) {
      setRows([])
      return
    }
    const { isInput, type } = filterFor(tab)
    const { data, errors } = await getSubmissions({
      companyId,
      isInput,
      type,
      status: status ?? "",
      dateStart,
      dateEnd,
      page: 0,
      limit: 99999,
    })
    if (seq !== fetchSeqRef.current) return
    if (errors) {
      handleToast(errors, "error")
      return
    }
    const items = data?.submissions ?? []
    setRows(
      items.map((s) => ({
        key: `m_${s.id}`,
        row: s as ManagedRow,
      })),
    )
    setContaminationByKey({})
  }, [companyId, dateEnd, dateStart, status, tab])

  useEffect(() => {
    fetchRows()
  }, [fetchRows])

  useEffect(() => {
    dispatch(fetchCountries())
    getPlants().then((d) => {
      if (d.data) setPlants(d.data as Company[])
    })
  }, [dispatch])

  useEffect(() => {
    if (tab === "convertingOutput") {
      getProducts({}).then((d) => {
        if (d.data) setProducts(flattenProducts(d.data as Product[]))
      })
    }
  }, [tab])

  const compositionTypeForTab = (currentTab: ManagedTab): CompositionType =>
    currentTab === "sortingInput" || currentTab === "sortingOutput"
      ? CompositionType.SORTING_OUTPUT
      : CompositionType.WASTE_ORIGIN

  const currentCompositionType = compositionTypeForTab(tab)
  const requestedCompositionsRef = useRef<Set<string>>(new Set())

  useEffect(() => {
    requestedCompositionsRef.current.clear()
    setCompositionsByCompany({})
  }, [currentCompositionType])

  const ensureCompositions = useCallback(
    (companyId: number) => {
      const cacheKey = `${companyId}:${currentCompositionType}`
      if (requestedCompositionsRef.current.has(cacheKey)) return
      requestedCompositionsRef.current.add(cacheKey)
      compositionGetByCompanyId({
        companyId,
        type: currentCompositionType,
      } as any).then(({ data }) => {
        if (data) {
          setCompositionsByCompany((p) => ({
            ...p,
            [companyId]: data as Composition[],
          }))
        }
      })
    },
    [currentCompositionType],
  )

  const derivedContamination = useCallback((row: ManagedRow): number => {
    const gross = row.volumeGross
    const net = row.volumeNet
    if (gross == null || gross <= 0 || net == null) return 0
    return Math.max(0, Math.round((1 - net / gross) * 10000) / 100)
  }, [])

  const updateRow = useCallback(
    (key: string, patch: Partial<ManagedRow>) => {
      setRows((prev) =>
        prev.map((r) =>
          r.key === key ? { ...r, row: { ...r.row, ...patch } } : r,
        ),
      )
    },
    [],
  )

  const handleRemove = useCallback(
    async (key: string) => {
      const target = rows.find((r) => r.key === key)
      clearRowState(key)
      if (target?.row.id != null) {
        const { errors } = await deleteSubmission({ id: target.row.id })
        if (errors) {
          handleToast(errors, "error")
          return
        }
      }
      setRows((prev) => prev.filter((r) => r.key !== key))
    },
    [clearRowState, rows],
  )

  const handleSave = useCallback(async () => {
    const { ok, savedCount } = await submitAll(
      rows.map(({ key, row }) => ({ key, row: { ...row, _key: key } })),
    )
    if (ok && savedCount === 0) {
      handleToast(
        [t("Nothing to save — no editable rows.") as string],
        "error",
      )
      return
    }
    if (ok) {
      handleToast([t("Changes saved") as string])
      setIsEditing(false)
      await fetchRows()
    } else {
      handleToast(
        [
          t("Some rows could not be saved. Check the highlighted rows.") as string,
        ],
        "error",
      )
    }
  }, [fetchRows, rows, submitAll, t])

  // --- Columns per tab ---

  const columns = useMemo<ColumnDef<LocalRow, any>[]>(() => {
    // Read-only plant name (used in tabs where plant is not editable)
    const plantReadOnly: ColumnDef<LocalRow, any> = {
      id: "plant",
      header: () => <span>{t("Plant name")}</span>,
      cell: ({ row }) => <span>{row.original.row.company?.name ?? "-"}</span>,
      size: 160,
    }

    // Editable plant dropdown
    const plantEditable: ColumnDef<LocalRow, any> = {
      id: "plant",
      header: () => <span>{t("Plant name")}</span>,
      cell: ({ row }) => {
        const r = row.original.row
        const currentId = r.company?.id
        return (
          <select
            value={currentId ?? ""}
            onChange={(e) => {
              const id = e.target.value ? Number(e.target.value) : undefined
              const found = plants.find((p) => p.id === id)
              updateRow(row.original.key, {
                company: found ?? r.company,
              })
              if (id) ensureCompositions(id)
            }}
          >
            <option value="">{t("Select…")}</option>
            {plants.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        )
      },
      size: 180,
    }

    const date: ColumnDef<LocalRow, any> = {
      id: "date",
      header: () => <span>{t("Date")}</span>,
      cell: ({ row }) => {
        const r = row.original.row
        const value = r.collectionDate ? new Date(r.collectionDate) : undefined
        return (
          <DatePicker
            selected={value}
            dateFormat="MMM-yy"
            showMonthYearPicker
            showPopperArrow={false}
            onChange={(d: Date) =>
              updateRow(row.original.key, {
                collectionDate: d.toISOString(),
              })
            }
            className="form-control"
            placeholderText={t("Pick month")}
          />
        )
      },
      size: 120,
    }

    // Editable waste origin (composition) dropdown
    const wasteOriginEditable: ColumnDef<LocalRow, any> = {
      id: "wasteOrigin",
      header: () => <span>{t("Waste origin")}</span>,
      cell: ({ row }) => {
        const r = row.original.row
        const cid = r.company?.id
        if (cid && !compositionsByCompany[cid]) ensureCompositions(cid)
        const list = cid
          ? filterCompanyCompositions<Composition>(
              compositionsByCompany[cid] ?? [],
              undefined,
              true,
            )
          : []
        const currentId = r.composition?.id
        const currentInList = currentId != null && list.some((c) => c.id === currentId)
        const options = currentId != null && !currentInList && r.composition
          ? [...list, r.composition as Composition]
          : list
        return (
          <select
            value={currentId ?? ""}
            onChange={(e) => {
              const id = e.target.value ? Number(e.target.value) : undefined
              const found = options.find((c) => c.id === id)
              updateRow(row.original.key, {
                composition: found ?? undefined,
              })
            }}
          >
            <option value="">{t("Select…")}</option>
            {options.map((c) => (
              <option key={c.id} value={c.id}>
                {c.productName}
              </option>
            ))}
          </select>
        )
      },
      size: 200,
    }

    // Read-only waste origin (composition) for tabs that don't edit it
    const wasteOriginReadOnly: ColumnDef<LocalRow, any> = {
      id: "wasteOrigin",
      header: () => <span>{t("Waste origin")}</span>,
      cell: ({ row }) => (
        <span>{row.original.row.composition?.productName ?? "-"}</span>
      ),
      size: 180,
    }

    // Editable "waste received from" (country) dropdown
    const wasteFromEditable: ColumnDef<LocalRow, any> = {
      id: "wasteFrom",
      header: () => <span>{t("Waste received from")}</span>,
      cell: ({ row }) => {
        const r = row.original.row
        return (
          <select
            value={r.country?.id ?? ""}
            onChange={(e) => {
              const id = e.target.value ? Number(e.target.value) : undefined
              const found = countries.find((c) => c.id === id)
              updateRow(row.original.key, {
                country: found ?? undefined,
              })
            }}
          >
            <option value="">{t("Select…")}</option>
            {countries.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        )
      },
      size: 180,
    }

    const polymer: ColumnDef<LocalRow, any> = {
      id: "polymer",
      header: () => <span>{t("Polymer")}</span>,
      cell: ({ row }) => (
        <span>{row.original.row.materialsGroup?.name ?? "-"}</span>
      ),
      size: 140,
    }

    const extra: ColumnDef<LocalRow, any> = {
      id: "extra",
      header: () => <span>{t("Extra information")}</span>,
      cell: ({ row }) => {
        const r = row.original.row
        return (
          <input
            type="text"
            placeholder={t("Free text")}
            value={r.administrationNumber ?? ""}
            onChange={(e) =>
              updateRow(row.original.key, {
                administrationNumber: e.target.value,
              })
            }
          />
        )
      },
      size: 180,
    }

    const volumeGross: ColumnDef<LocalRow, any> = {
      id: "volumeGross",
      header: () => <span>{t("Input (Total) (kg)")}</span>,
      cell: ({ row }) => {
        const r = row.original.row
        return (
          <input
            type="number"
            min={0}
            step="any"
            value={r.volumeGross ?? ""}
            onChange={(e) => {
              const gross = e.target.value ? parseFloat(e.target.value) : 0
              updateRow(row.original.key, { volumeGross: gross })
            }}
          />
        )
      },
      size: 130,
    }

    const contamination: ColumnDef<LocalRow, any> = {
      id: "contamination",
      header: () => <span>{t("Contamination (%)")}</span>,
      cell: ({ row }) => {
        const key = row.original.key
        const edited = contaminationByKey[key]
        const display =
          edited != null ? edited : derivedContamination(row.original.row)
        return (
          <input
            type="number"
            min={0}
            max={100}
            step="any"
            placeholder={t("0")}
            value={Number.isFinite(display) ? display : ""}
            onChange={(e) => {
              const raw = e.target.value
              setContaminationByKey((prev) => ({
                ...prev,
                [key]: raw === "" ? undefined : parseFloat(raw),
              }))
            }}
          />
        )
      },
      size: 120,
    }

    const volumeNetReadOnly: ColumnDef<LocalRow, any> = {
      id: "volumeNet",
      header: () => {
        const tip = isSorter
          ? t("Input into the sorting operations")
          : isSorterAndRecycler
          ? t("Input into recycling and sorting operations (kg)")
          : t("Input into the final recycling operation (kg)")
        return (
          <span>
            {t("Input (net) (kg)")}
            <span className="srs-grid-label-info" title={tip}>
              (i)
            </span>
          </span>
        )
      },
      cell: ({ row }) => {
        const r = row.original.row
        const key = row.original.key
        const editedPct = contaminationByKey[key]
        const value =
          editedPct != null
            ? computeNet(r.volumeGross, editedPct)
            : r.volumeNet ?? 0
        return (
          <input
            type="number"
            readOnly
            value={value}
            style={{ background: "#f0f0f0", cursor: "not-allowed" }}
          />
        )
      },
      size: 140,
    }

    const netEU: ColumnDef<LocalRow, any> = {
      id: "netOutputEU",
      header: () => <span>{t("Net output EU+UK (kg)")}</span>,
      cell: ({ row }) => {
        const r = row.original.row
        return (
          <input
            type="number"
            min={0}
            step="any"
            value={r.netOutputEU ?? ""}
            onChange={(e) =>
              updateRow(row.original.key, {
                netOutputEU: e.target.value ? parseFloat(e.target.value) : 0,
              })
            }
          />
        )
      },
      size: 150,
    }

    const netNonEU: ColumnDef<LocalRow, any> = {
      id: "netOutputNonEU",
      header: () => <span>{t("Net output Non-EU (kg)")}</span>,
      cell: ({ row }) => {
        const r = row.original.row
        return (
          <input
            type="number"
            min={0}
            step="any"
            value={r.netOutputNonEU ?? ""}
            onChange={(e) =>
              updateRow(row.original.key, {
                netOutputNonEU: e.target.value ? parseFloat(e.target.value) : 0,
              })
            }
          />
        )
      },
      size: 150,
    }

    // Editable "recycled plastic from" (country) dropdown for converting input
    const recycledFromEditable: ColumnDef<LocalRow, any> = {
      id: "recycledFrom",
      header: () => <span>{t("Recycled plastic from")}</span>,
      cell: ({ row }) => {
        const r = row.original.row
        return (
          <select
            value={r.country?.id ?? ""}
            onChange={(e) => {
              const id = e.target.value ? Number(e.target.value) : undefined
              const found = countries.find((c) => c.id === id)
              updateRow(row.original.key, {
                country: found ?? undefined,
              })
            }}
          >
            <option value="">{t("Select…")}</option>
            {countries.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        )
      },
      size: 180,
    }

    const total: ColumnDef<LocalRow, any> = {
      id: "total",
      header: () => <span>{t("Total (kg)")}</span>,
      cell: ({ row }) => {
        const r = row.original.row
        return (
          <input
            type="number"
            min={0}
            step="any"
            value={r.volumeNet ?? ""}
            onChange={(e) =>
              updateRow(row.original.key, {
                volumeNet: e.target.value ? parseFloat(e.target.value) : 0,
              })
            }
          />
        )
      },
      size: 130,
    }

    const usedIn: ColumnDef<LocalRow, any> = {
      id: "usedIn",
      header: () => <span>{t("Used In")}</span>,
      cell: ({ row }) => {
        const r = row.original.row
        return (
          <select
            value={r.productId ?? ""}
            onChange={(e) =>
              updateRow(row.original.key, {
                productId: e.target.value ? Number(e.target.value) : undefined,
              })
            }
          >
            <option value="">{t("Select…")}</option>
            {products.map((p) => (
              <option key={p.id} value={p.id}>
                {p.title}
              </option>
            ))}
          </select>
        )
      },
      size: 180,
    }

    const shareFrance: ColumnDef<LocalRow, any> = {
      id: "shareFrance",
      header: () => <span>{t("Products for the French market (%)")}</span>,
      cell: ({ row }) => {
        const r = row.original.row
        return (
          <input
            type="number"
            min={0}
            max={100}
            step="any"
            value={r.totalPercentageOfRecycledPlasticsInFrance ?? ""}
            onChange={(e) =>
              updateRow(row.original.key, {
                totalPercentageOfRecycledPlasticsInFrance: e.target.value
                  ? parseFloat(e.target.value)
                  : 0,
              })
            }
          />
        )
      },
      size: 160,
    }

    const shareEu: ColumnDef<LocalRow, any> = {
      id: "shareEu",
      header: () => (
        <span>{t("Products exported outside EU/UK/Norway/Switzerland (%)")}</span>
      ),
      cell: ({ row }) => {
        const r = row.original.row
        return (
          <input
            type="number"
            min={0}
            max={100}
            step="any"
            value={r.estimatedShareExportedInsideEu ?? ""}
            onChange={(e) =>
              updateRow(row.original.key, {
                estimatedShareExportedInsideEu: e.target.value
                  ? parseFloat(e.target.value)
                  : 0,
              })
            }
          />
        )
      },
      size: 200,
    }

    const shareNonEu: ColumnDef<LocalRow, any> = {
      id: "shareNonEu",
      header: () => (
        <span>{t("Products exported to Norway and Switzerland (%)")}</span>
      ),
      cell: ({ row }) => {
        const r = row.original.row
        return (
          <input
            type="number"
            min={0}
            max={100}
            step="any"
            value={r.estimatedShareExportedOutsideEu ?? ""}
            onChange={(e) =>
              updateRow(row.original.key, {
                estimatedShareExportedOutsideEu: e.target.value
                  ? parseFloat(e.target.value)
                  : 0,
              })
            }
          />
        )
      },
      size: 200,
    }

    switch (tab) {
      case "input":
      case "sortingInput":
        return [plantReadOnly, wasteOriginEditable, date, wasteFromEditable, volumeGross, contamination, volumeNetReadOnly, extra]
      case "output":
        return [plantEditable, polymer, date, wasteOriginEditable, netEU, netNonEU, extra]
      case "sortingOutput":
        return [plantEditable, polymer, date, wasteOriginReadOnly, netEU, netNonEU, extra]
      case "convertingInput":
        return [plantEditable, polymer, wasteOriginEditable, date, recycledFromEditable, total, extra]
      case "convertingOutput":
        return [plantEditable, polymer, date, usedIn, total, shareFrance, shareEu, shareNonEu, extra]
    }
  }, [
    compositionsByCompany,
    contaminationByKey,
    countries,
    derivedContamination,
    ensureCompositions,
    isSorter,
    isSorterAndRecycler,
    plants,
    products,
    setContaminationByKey,
    t,
    tab,
    updateRow,
  ])

  return (
    <div>
      <div className="srs-grid-toolbar">
        {!isEditing ? (
          <button
            type="button"
            className="btn btn-outline-primary"
            onClick={() => setIsEditing(true)}
            disabled={rows.length === 0}
          >
            {t("Edit")}
          </button>
        ) : (
          <>
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() => {
                setIsEditing(false)
                fetchRows()
              }}
              disabled={isSubmitting}
            >
              {t("Cancel")}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSave}
              disabled={isSubmitting}
            >
              {isSubmitting ? t("Saving…") : t("Save")}
            </button>
          </>
        )}
      </div>
      <SubmissionGrid<ManagedRow>
        rows={rows}
        columns={columns}
        states={states}
        onAdd={() => {}}
        onRemove={handleRemove}
        onSubmit={handleSave}
        isSubmitting={isSubmitting}
        isReadOnly={!isEditing}
        hideFooterSave
      />
    </div>
  )
}

export default ManageSubmissionsGrid
